import React, {useState} from 'react';
import styled from 'styled-components';
import listService from 'ListService';
import { toast } from '../Common/ToastNotification';
import { Button } from '../Common/StyledComponents';
import ConfirmModal from '../Common/ConfirmModal.js';
import zLib from 'util/ZLib.js';

const Container = styled.div`
  padding: 15px;
  font-size: 0.9em;
  color: #777;
  box-sizing: border-box;
  display: flex;
  gap: 10px;
  justify-content: center;
  align-items: center;
`;

// Define our unique separators
const BLOCK_HEADER_START = "// >>> BLOCK-START";
const BLOCK_FOOTER = "// >>> BLOCK-END <<<";

function ExportPanel({ objectGroups, allCss, setAllCss, setIsDirty, isDirty, handleSave }) {
    const [modalOpen, setModalOpen] = useState(false);
    const [fileContentToImport, setFileContentToImport] = useState(null);

    const handleExportAll = async () => {

        if (isDirty) {
            const saveSuccessful = await handleSave();

            // If the save failed for any reason, stop the export
            if (!saveSuccessful) {
                toast.show({ 
                    type: 'error', 
                    message: 'Save failed. Export has been cancelled.' 
                });
                return;
            }
        }
        
        const cssBlocks = [];

        // Iterate through the defined order of object groups
        objectGroups.forEach(group => {
            const groupCss = allCss[group.id];
            cssBlocks.push(`${BLOCK_HEADER_START} | OBJECT: ${group.name} | <<<`);
            cssBlocks.push(groupCss);
            cssBlocks.push(BLOCK_FOOTER);
        });

        const css = cssBlocks.join('\n\n');
        const fileContent = zLib.compressForm(css);
        const obj = listService.getObject("aForm");
        const fileName = obj.model.get("name").replace(" ", "_") + "_advCSS.pa";

        const blob = new Blob([fileContent], { type: 'text/css;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);

        toast.show({ type: 'success', message: 'Advanced CSS exported successfully.' });
    };

    const handleImportFileSelect = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pa';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            try {
                const content = await file.text();
                const decompressedContent = zLib.decompressForm( content );
                setFileContentToImport(decompressedContent);
                setModalOpen(true); // Open confirmation modal
            } catch (err) {
                toast.show({ type: 'error', message: `Failed to read file: ${err.message}` });
            }
        };
        input.click();
    };

    const handleImportConfirmed = () => {
        if (!fileContentToImport) return;

        try {
            const newCssState = { ...allCss }; // Start with current state as a base
            let blocksFound = 0;

            const blockRegex = new RegExp(`${BLOCK_HEADER_START.replace('|', '\\|')} \\| OBJECT: (.*?) \\| <<<([\\s\\S]*?)${BLOCK_FOOTER}`, 'g');

            let match;
            while ((match = blockRegex.exec(fileContentToImport)) !== null) {
                const objectName = match[1].trim();
                const cssContent = match[2].trim();

                const group = objectGroups.find(g => g.name === objectName);
                if (group) {
                    newCssState[group.id] = cssContent;
                    blocksFound++;
                }
            }

            if (blocksFound > 0) {
                setAllCss(newCssState);
                setIsDirty(true);
                toast.show({ type: 'success', message: `Successfully imported ${blocksFound} style blocks.` });
            } else {
                throw new Error('The selected file is not a valid PerfectApps Advanced CSS template.');
            }

        } catch (err) {
            console.error("Failed to import file", err);
            toast.show({ type: 'error', message: `Import failed: ${err.message}` });
        } finally {
            setModalOpen(false);
            setFileContentToImport(null);
        }
    };

    return (
        <>
            <Container>
                <Button variant="orange" onClick={handleImportFileSelect}>Import All</Button>
                <Button variant="orange" onClick={handleExportAll}>Export All</Button>
            </Container>

            {modalOpen && (
                <ConfirmModal
                    title="Import Styles"
                    onClose={() => setModalOpen(false)}
                    buttons={[
                        { label: 'Cancel', variant: 'default', onClick: () => setModalOpen(false) },
                        { label: 'Import and Replace', variant: 'orange', onClick: handleImportConfirmed }
                    ]}
                >
                    <div>
                        Are you sure you want to import styles from this file?
                        This will override any existing custom styles.
                    </div>
                </ConfirmModal>
            )}
        </>
    );
}

export default ExportPanel;